/**
 * Makes an API call.
 * @param {string} command - The command to be sent to the API.
 * @param {Object} otherData - Additional data to be included in the API call.
 * @returns {Promise} A promise that resolves to the API response.
 */
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";
import { Prompt } from "/WebInterface/new-ui/modules/alert.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";

const CHALLENGE_OTP_PREFIX = "CHALLENGE_OTP:";

const extractCommandResponseText = (result) => {
  if (result == null) return "";

  if (typeof result === "object") {
    const response = result?.commandResult?.response;
    if (typeof response === "string") return response;
    if (response != null) return String(response);
    return "";
  }

  if (typeof result !== "string") return String(result);

  const trimmed = result.trim();
  if (!trimmed.startsWith("<")) return trimmed;

  try {
    const xml = new DOMParser().parseFromString(trimmed, "text/xml");
    const parsed = utils.xmlToJson(xml);
    const response = parsed?.commandResult?.response;
    if (typeof response === "string") return response;
    if (response != null) return String(response);
  } catch (error) {
    console.log(error);
  }

  return trimmed;
};

const isOtpChallengeResponse = (responseText) =>
  typeof responseText === "string" &&
  responseText.startsWith(CHALLENGE_OTP_PREFIX);

const promptForOtpToken = () => {
  const raw = t("otpInstruction", {}, "");
  const otpInstruction =
    raw === "otpInstruction" || !raw ? "" : String(raw).trim();

  const ask = () =>
    Prompt({
      title: t("enterYourOTPHere"),
      instructionLabel: otpInstruction,
      placeholder: "000000",
      confirm: t("submit"),
      cancel: "",
      closeOnOutsideClick: false,
    }).then((res) => {
      if (!res) return Promise.reject({ canceled: true });
      const value = (res.value || "").trim();
      if (!value) return ask();
      return value;
    });

  return ask();
};

const handleOtpChallenge = async (apiFn, data, result) => {
  if (!isOtpChallengeResponse(extractCommandResponseText(result))) {
    return result;
  }
  const otp_token = await promptForOtpToken();
  return apiFn({ ...data, otp_token });
};

export const apiCall = (command, otherData = {}) => {
  return new Promise((resolve, reject) => {
    const c2f = utils.cookie.get("currentAuth");
    if (c2f) {
      ajax
        .call({
          data: {
            command: command,
            c2f,
            ...otherData,
          },
        })
        .then((resp) => {
          // Sanitize response to remove inline styles for CSP compliance
          // This prevents CSP violations when XML/HTML with inline styles is parsed and inserted into DOM
          let sanitizedResp = resp;
          // if (typeof resp === "string") {
          //   // Remove inline style attributes to prevent CSP violations
          //   // Match: style="..." or style='...' or style=... (with or without quotes, with or without spaces)
          //   sanitizedResp = resp.replace(/\s+style\s*=\s*["'][^"']*["']/gi, "");
          //   sanitizedResp = sanitizedResp.replace(
          //     /\s+style\s*=\s*[^\s>]+/gi,
          //     ""
          //   );
          //   // Also handle style attributes that might be at the start of a tag
          //   sanitizedResp = sanitizedResp.replace(
          //     /style\s*=\s*["'][^"']*["']/gi,
          //     ""
          //   );
          //   sanitizedResp = sanitizedResp.replace(/style\s*=\s*[^\s>]+/gi, "");
          // }

          if (command === "publish") {
            let xmlString = sanitizedResp;

            xmlString = xmlString.replace(
              /<password>(.*?)<\/password>/,
              (match, p1) => {
                const encodedPassword = p1
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;");
                return `<password>${encodedPassword}</password>`;
              }
            );

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, "application/xml");

            const username = xmlDoc.querySelector("username")?.textContent;
            const password = xmlDoc.querySelector("password")?.textContent;
            const expireDate = xmlDoc.querySelector("expire_date")?.textContent;
            const message = xmlDoc.querySelector("message")?.textContent || "";
            const url = xmlDoc.querySelector("url")?.textContent || "";
            resolve({
              commandResult: {
                response: { username, password, expireDate, message, url },
              },
            });
          } else {
            // Only proceed if sanitizedResp is a string
            if (typeof sanitizedResp !== "string") {
              reject({ message: "Invalid response format: expected string" });
              return;
            }
            const xml = new DOMParser().parseFromString(
              sanitizedResp,
              "text/xml"
            );
            // Remove inline style attributes from parsed DOM tree for CSP compliance
            if (xml && xml.documentElement) {
              xml.querySelectorAll("*").forEach(function (el) {
                if (el.hasAttribute && el.hasAttribute("style")) {
                  el.removeAttribute("style");
                }
              });
            }
            const result = utils.xmlToJson(xml);
            resolve(result);
          }
        })
        .fail((err) => {
          console.log(err);
          reject(err);
        });
    } else {
      reject({ message: "Not logged in" });
    }
  });
};

/**
 * Retrieves the user's information.
 * @returns {Promise} A promise that resolves to the user's information.
 */
export const getUserInfo = () => {
  return new Promise((resolve, reject) => {
    apiCall("getUserInfo")
      .then((result) => {
        if (result && result.userInfo) {
          const { username } = result.userInfo;
          if (username) {
            resolve(result.userInfo);
          } else {
            reject();
          }
        } else {
          reject();
        }
      })
      .catch((ex) => reject(ex));
  });
};

/**
 * Checks if the user is logged in.
 * @returns {Promise} A promise that resolves to the login result if the user is logged in.
 */
export const isLoggedIn = () => {
  return new Promise((resolve, reject) => {
    apiCall("getUsername")
      .then((result) => {
        if (result && result.loginResult) {
          const { response } = result.loginResult;
          if (response == "success") {
            resolve(result.loginResult);
          } else {
            reject();
          }
        } else {
          reject();
        }
      })
      .catch((ex) => reject(ex));
  });
};

/**
 * Logs out the user.
 * @param {string} redirectLocation - The location to redirect to after logout.
 * @returns {Promise} A promise that resolves after the user is logged out.
 */
export const doLogout = (redirectLocation = "/", skipRedirect = false) => {
  window.isLoggingOut = true;
  return new Promise((resolve, reject) => {
    apiCall("logout")
      .then(() => {
        utils.cookie.remove("currentAuth");
        if (!skipRedirect) {
          window.location = redirectLocation;
        }
      })
      .catch((ex) => {
        reject("Logout failed");
      })
      .finally(() => {
        resolve();
      });
  });
};

const currentListingAPICalls = [];

/**
 * Retrieves the listing from the given path.
 * @param {string} path - The path of the listing.
 * @returns {Promise} A promise that resolves to the listing data.
 */
export const getListingFromPath = (path = "/", abortExisting = true) => {
  return new Promise((resolve, reject) => {
    if (abortExisting) {
      currentListingAPICalls.forEach((xhr) => xhr.abort());
      currentListingAPICalls.length = 0;
    }
    const defaultResponse = {
      privs: "(view)",
      failed: true,
      comment: "Something went wrong while fetching the file list",
      path: "/",
      defaultStrings: "",
      site: "",
      quota: "",
      quota_bytes: "",
      bytes_sent: "0",
      bytes_received: "0",
      max_upload_amount_day: "0",
      max_upload_amount_month: "0",
      max_upload_amount: "0",
      max_upload_amount_available: "0",
      max_upload_amount_day_available: "",
      max_upload_amount_month_available: "",
      max_download_amount: "0",
      max_download_amount_day: "0",
      max_download_amount_month: "0",
      max_download_amount_available: "0",
      max_download_amount_day_available: "",
      max_download_amount_month_available: "",
      listing: [],
    };

    const xhr = ajax
      .call({
        data: {
          command: "getXMLListing",
          format: "JSONOBJ",
          path: utils.encodeURILocal(path.endsWith("/") ? path : path + "/"),
        },
        timeout: 10000000,
      })
      .then((data) => {
        const index = currentListingAPICalls.indexOf(xhr);
        if (index !== -1) {
          currentListingAPICalls.splice(index, 1);
        }
        if (data) {
          if (data.error_msg) {
            // a refusal ("ERROR: <name>: invalid name, not allowed.") is passed on for the UI to show; the server url-encodes it
            const errorMsg = utils.decodeURILocal(String(data.error_msg));
            resolve(
              errorMsg.startsWith("ERROR:")
                ? { ...defaultResponse, error_msg: errorMsg }
                : defaultResponse
            );
            return;
          }
          resolve(data);
        } else {
          if (index === -1) {
            reject();
          } else {
            resolve(defaultResponse);
          }
        }
      })
      .catch((ex) => {
        const index = currentListingAPICalls.indexOf(xhr);
        if (index !== -1) {
          currentListingAPICalls.splice(index, 1);
        }
        // a request superseded by a newer listing stays pending, as before, so its caller never renders it;
        // any other failure (e.g. invalid JSON) settles so the folder doesn't stay loading
        if (xhr && xhr._aborted) return;
        resolve(defaultResponse);
      });
    currentListingAPICalls.push(xhr);
  });
};

/**
 * Retrieves the custom form with the given name.
 * @param {string} formName - The name of the custom form.
 * @returns {Promise} A promise that resolves to the custom form data.
 */
export const getCustomForm = (
  formName = "messageForm",
  isCustomEvent = false
) => {
  return new Promise((resolve, reject) => {
    apiCall("getCustomForm", { form: formName })
      .then((result) => {
        if (isCustomEvent) {
          resolve(result?.customForm);
        } else if (result?.customForm) {
          resolve(result?.customForm);
        } else {
          reject();
        }
      })
      .catch((ex) => reject(ex));
  });
};

/**
 * Posts the data to a custom form.
 * @param {Object} data - The data to be posted to the custom form.
 * @returns {Promise} A promise that resolves after the form is submitted successfully.
 */
export const postCustomForm = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("messageForm", data)
      .then((result) => {
        const resp = result?.commandResult?.response;
        if (resp === "Success") {
          resolve(result);
        } else {
          reject(resp || "Failure");
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * Posts the data to perform a search.
 * @param {Object} data - The data to be used for the search.
 * @returns {Promise} A promise that resolves to the search result.
 */
export const postSearchForm = (data = {}) => {
  return new Promise((resolve, reject) => {
    const c2f = utils.cookie.get("currentAuth");
    ajax
      .call({
        data: {
          command: "search",
          c2f,
          ...data,
        },
        timeout: 10000000,
      })
      .then((result) => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * Changes the user's password.
 * @param {Object} data - The data containing the new password.
 * @returns {Promise} A promise that resolves to the result of changing the password.
 */
export const changePassword = (data = {}) => {
  return new Promise((resolve, reject) => {
    const c2f = utils.cookie.get("currentAuth");
    ajax
      .call({
        data: {
          command: "changePassword",
          c2f,
          ...data,
        },
      })
      .then((resp) => {
        if (resp) {
          // Sanitize response to remove inline styles for CSP compliance
          let sanitizedResp = resp;
          if (typeof resp === "string") {
            sanitizedResp = resp.replace(/\s+style\s*=\s*["'][^"']*["']/gi, "");
            sanitizedResp = sanitizedResp.replace(
              /\s+style\s*=\s*[^\s>]+/gi,
              ""
            );
          }
          // Only proceed if sanitizedResp is a string
          if (typeof sanitizedResp !== "string") {
            reject({ message: "Invalid response format: expected string" });
            return;
          }
          const xml = new DOMParser().parseFromString(
            sanitizedResp,
            "text/xml"
          );
          // Remove inline style attributes from parsed DOM tree for CSP compliance
          if (xml && xml.documentElement) {
            xml.querySelectorAll("*").forEach(function (el) {
              if (el.hasAttribute && el.hasAttribute("style")) {
                el.removeAttribute("style");
              }
            });
          }
          const result = utils.xmlToJson(xml);
          handleOtpChallenge(changePassword, data, result)
            .then(resolve)
            .catch(reject);
        } else {
          reject();
        }
      })
      .catch((ex) => {
        reject(ex);
      });
  });
};

/**
 * Changes the user's phone number.
 * @param {Object} data - The data containing the new phone number.
 * @returns {Promise} A promise that resolves to the result of changing the phone number.
 */
export const changePhone = (data = {}) => {
  return new Promise((resolve, reject) => {
    const c2f = utils.cookie.get("currentAuth");
    ajax
      .call({
        data: {
          command: "changePhone",
          c2f,
          ...data,
        },
      })
      .then((resp) => {
        if (resp) {
          handleOtpChallenge(changePhone, data, resp)
            .then(resolve)
            .catch(reject);
        } else {
          reject();
        }
      })
      .catch((ex) => {
        reject(ex);
      });
  });
};

/**
 * Confirms a QR code.
 * @param {Object} data - The data containing the QR code information.
 * @returns {Promise} A promise that resolves to the confirmation result.
 */
export const confirmQR = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("confirmQR", data)
      .then((result) => {
        if (
          result.commandResult &&
          result.commandResult.response === "Success"
        ) {
          resolve(result.commandResult.response);
        } else {
          reject(result.commandResult.response);
        }
      })
      .catch((ex) => reject(ex));
  });
};

/**
 * Resets the user's password.
 * @param {Object} data - The data containing the necessary information for password reset.
 * @returns {Promise} A promise that resolves to the result of resetting the password.
 */
export const resetPassword = (data = {}) => {
  return new Promise((resolve, reject) => {
    const c2f = utils.cookie.get("currentAuth");
    ajax
      .call({
        data: {
          command: "request_reset",
          ...data,
        },
        urlEncoded: true,
      })
      .then((resp) => {
        if (resp) {
          // Sanitize response to remove inline styles for CSP compliance
          let sanitizedResp = resp;
          if (typeof resp === "string") {
            sanitizedResp = resp.replace(/\s+style\s*=\s*["'][^"']*["']/gi, "");
            sanitizedResp = sanitizedResp.replace(
              /\s+style\s*=\s*[^\s>]+/gi,
              ""
            );
          }
          // Only proceed if sanitizedResp is a string
          if (typeof sanitizedResp !== "string") {
            reject({ message: "Invalid response format: expected string" });
            return;
          }
          const xml = new DOMParser().parseFromString(
            sanitizedResp,
            "text/xml"
          );
          // Remove inline style attributes from parsed DOM tree for CSP compliance
          if (xml && xml.documentElement) {
            xml.querySelectorAll("*").forEach(function (el) {
              if (el.hasAttribute && el.hasAttribute("style")) {
                el.removeAttribute("style");
              }
            });
          }
          const result = utils.xmlToJson(xml);
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => reject(error));
  });
};

const callApiAndResolve = (command, data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall(command, data)
      .then((result) => {
        resolve(result.commandResult.response);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getAltDomainToken = () =>
  callApiAndResolve("loginDomain1", {
    random: crypto.getRandomValues(new Uint32Array(1))[0],
  });

export const reportAProblem = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("problemEvent", data)
      .then((result) => {
        if (result.commandResult?.response === "SUCCESS") {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getPreview = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("getPreview", data)
      .then((result) => {
        if (result.commandResult?.response === "SUCCESS") {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const setPreview = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("setPreview", data)
      .then((result) => {
        if (result.commandResult?.response === "SUCCESS") {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const batchComplete = (data = {}) => {
  return new Promise((resolve, reject) => {
    const c2f = utils.cookie.get("currentAuth");

    ajax
      .call({
        data: {
          command: "batchComplete",
          c2f,
        },
      })
      .then((resp) => {
        resolve(resp);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getSearchStatus = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("getSearchStatus", data)
      .then((result) => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getSubscribeReverseNotificationEvents = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("getSubscribeReverseNotificationEvents", data)
      .then((result) => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const saveSubscribeReverseNotificationEvents = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("saveSubscribeReverseNotificationEvents", data)
      .then((result) => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getAllSubscribeReverseNotificationEvents = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("getAllSubscribeReverseNotificationEvents", data)
      .then((result) => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const verifyEmail = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("verifyEmail", data)
      .then((result) => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const verifyEmailToken = (data = {}) => {
  return new Promise((resolve, reject) => {
    apiCall("verifyEmailConfirm", data)
      .then((result) => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};
